// src/components/dashboard/widgets/transport-widget.tsx
import { useEffect, useState } from 'react';
import { AlertTriangle, TrainFront } from 'lucide-react';
import { supabase } from '@/src/lib/supabase';
import { useAppColors } from '@/src/lib/theme';
import type { NearbyStop } from '@/src/components/dashboard/widgets/nearby-transport-picker';

interface AlertSummary { header: string; description: string }
interface TransportData { totalStatewide: number; relevant: AlertSummary[]; filtered: boolean }

interface TransportWidgetProps {
  nearbyStops: NearbyStop[];
}

export default function TransportWidget({ nearbyStops }: TransportWidgetProps) {
  const c = useAppColors();
  const [data, setData] = useState<TransportData | undefined>(undefined);

  useEffect(() => {
    supabase.functions.invoke('transport-alerts', {
      body: { stopIds: nearbyStops.map((s) => s.id) },
    }).then(({ data: d }) => {
      setData(d?.ok ? { totalStatewide: d.totalStatewide ?? 0, relevant: d.relevant ?? [], filtered: !!d.filtered } : { totalStatewide: 0, relevant: [], filtered: false });
    });
  }, [nearbyStops]);

  const hasAlerts = data && data.filtered && data.relevant.length > 0;

  return (
<div
  className={hasAlerts ? 'rounded-2xl border p-4 h-full' : 'rounded-2xl border p-4 h-full bg-warm-gray'}
  style={hasAlerts
    ? { background: 'rgba(232,116,106,0.07)', borderColor: 'rgba(232,116,106,0.35)' }
    : { borderColor: 'rgba(74,154,74,0.2)' }
  }
>
      <div className="flex items-center justify-between mb-3">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={hasAlerts ? { background: 'rgba(232,116,106,0.15)' } : { background: 'rgba(74,154,74,0.1)' }}
        >
          {hasAlerts ? (
            <AlertTriangle size={15} style={{ color: '#E8746A' }} />
          ) : (
            <TrainFront size={15} className="text-leaf-400" />
          )}
        </div>
        <span
          className="text-[9px] font-bold tracking-widest uppercase"
          style={hasAlerts ? { color: '#E8746A' } : { color: 'rgba(245,245,242,0.5)' }}
        >
          {hasAlerts ? 'Transport alert' : 'Transport (NSW)'}
        </span>
      </div>

      {data === undefined ? (
        <div className="text-xs text-cream-muted/50">Loading…</div>
      ) : !data.filtered ? (
        <div className="text-xs text-cream-muted/50">Add a nearby station to see relevant alerts</div>
      ) : data.relevant.length === 0 ? (
        <div className="text-xs text-cream-muted">Nothing affecting your area right now</div>
      ) : (
        <div className="space-y-2">
          {data.relevant.slice(0, 3).map((a, i) => (
            <div key={i} className="flex items-start gap-1.5">
              <span className="w-1 h-1 rounded-full mt-1.5 flex-shrink-0" style={{ background: '#E8746A' }} />
              <span className="text-[11px] leading-snug" style={{ color: '#F5D5D1' }}>{a.header}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}